import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DownloadSimple, ArrowSquareOut, ArrowsClockwise, Clock, Trash, Warning } from "@phosphor-icons/react";
import api, { apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { mediaSrc, fmtDateTime, CAPTURE_LABELS } from "./serviceApi";

const toLocalInput = (iso) => {
  if (!iso) return "";
  const m = iso.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  return m ? `${m[1]}T${m[2]}` : "";
};

export function PhotoViewer({ media, open, onOpenChange, onChanged, onDelete, canDelete, readOnly }) {
  const [showOriginal, setShowOriginal] = useState(false);
  const [editTime, setEditTime] = useState(false);
  const [timeVal, setTimeVal] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && media) { setShowOriginal(false); setEditTime(false); setTimeVal(toLocalInput(media.capture_at)); }
  }, [open, media]);

  if (!media) return null;
  const src = mediaSrc(showOriginal ? media.original_url : media.display_url);

  const act = async (fn, msg) => {
    setBusy(true);
    try { const { data } = await fn(); onChanged?.(data); toast.success(msg); } catch (e) { toast.error(apiErrorMessage(e, "Action failed")); } finally { setBusy(false); }
  };
  const restamp = () => act(() => api.post(`/service/media/${media.id}/restamp`), "Stamp regenerated from the original photo");
  const saveTime = () => act(() => api.put(`/service/media/${media.id}/capture-time`, { capture_local: timeVal }), "Capture time corrected and stamp regenerated").then(() => setEditTime(false));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-w-5xl max-h-[94vh] overflow-y-auto" data-testid="photo-viewer">
        <DialogHeader>
          <DialogTitle className="truncate pr-8" data-testid="photo-viewer-name">{media.display_name || media.filename}</DialogTitle>
        </DialogHeader>
        <div className="bg-black/90 rounded-lg flex items-center justify-center min-h-[300px]">
          <img src={src} alt={media.filename} className="max-h-[62vh] max-w-full object-contain" data-testid="photo-viewer-image" />
        </div>
        {media.stamp_outdated && !readOnly && (
          <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="photo-stamp-outdated">
            <Warning size={16} /> The service details changed since this stamp was made.
            <button type="button" onClick={restamp} className="ml-auto font-semibold underline" data-testid="photo-stamp-outdated-regen">Regenerate now</button>
          </div>
        )}
        {media.stamp_status === "needs_info" && (
          <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">Add the address and category to this entry, then regenerate the stamp.</div>
        )}
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" role="tablist">
            <button type="button" onClick={() => setShowOriginal(false)} className={`px-3 py-1.5 text-xs font-semibold transition-colors ${!showOriginal ? "bg-[#327de3] text-white" : "text-slate-600 hover:bg-slate-50"}`} data-testid="photo-view-stamped">Stamped</button>
            <button type="button" onClick={() => setShowOriginal(true)} className={`px-3 py-1.5 text-xs font-semibold transition-colors ${showOriginal ? "bg-[#327de3] text-white" : "text-slate-600 hover:bg-slate-50"}`} data-testid="photo-view-original">Original</button>
          </div>
          <a href={mediaSrc((showOriginal ? media.original_url : media.display_url) + "&download=1")} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50" data-testid="photo-download-btn">
            <DownloadSimple size={13} /> Download {showOriginal ? "original" : "stamped"}
          </a>
          <a href={src} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50" data-testid="photo-open-tab-btn">
            <ArrowSquareOut size={13} /> Open in new tab
          </a>
          {!readOnly && <Button size="sm" variant="outline" onClick={restamp} disabled={busy} data-testid="photo-restamp-btn">
            <ArrowsClockwise size={13} className="mr-1" /> Regenerate stamp
          </Button>}
          {canDelete && !readOnly && (
            <Button size="sm" variant="outline" onClick={() => onDelete(media)} className="ml-auto text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="photo-delete-btn">
              <Trash size={13} className="mr-1" /> Delete
            </Button>
          )}
        </div>
        <div className="rounded-md border border-slate-200 px-3 py-2.5 text-sm flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="inline-flex items-center gap-1.5 text-slate-700">
            <Clock size={14} className="text-slate-400" />
            <span className="font-semibold" data-testid="photo-capture-time">{(media.capture_display || fmtDateTime(media.capture_at))}</span>
            <span className="text-xs text-slate-500" data-testid="photo-capture-source">· {CAPTURE_LABELS[media.capture_source] || media.capture_source}</span>
          </div>
          {readOnly ? null : editTime ? (
            <div className="flex items-center gap-1.5">
              <Input type="datetime-local" value={timeVal} onChange={(e) => setTimeVal(e.target.value)} className="h-8 w-auto" data-testid="photo-capture-time-input" />
              <Button size="sm" onClick={saveTime} disabled={busy || !timeVal} className="h-8 bg-[#327de3] hover:bg-[#1f64c2] text-white" data-testid="photo-capture-time-save">Save & restamp</Button>
              <Button size="sm" variant="ghost" onClick={() => setEditTime(false)} className="h-8">Cancel</Button>
            </div>
          ) : (
            <button type="button" onClick={() => setEditTime(true)} className="text-xs font-semibold text-[#327de3] hover:underline" data-testid="photo-capture-time-edit">Correct date/time</button>
          )}
          <div className="w-full text-[11px] text-slate-500">
            Uploaded {fmtDateTime(media.uploaded_at)}{media.uploaded_by?.name ? ` by ${media.uploaded_by.name}` : ""}
            {media.original_capture_at && ` · originally ${(media.original_capture_display || fmtDateTime(media.original_capture_at))} (${CAPTURE_LABELS[media.original_capture_source] || media.original_capture_source})`}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
